<template>
  <div class="mainportal-wrapper">
    <!-- ============================================
         News Slider Section
         ============================================ -->
    <div class="news-section">
      <!-- Header -->
      <div class="news-header">
        <div class="news-header-text">
          <div class="header-decorations">
            <span class="deco-star">✦</span>
            <span class="deco-sparkle">✧</span>
          </div>
          <h1 class="news-title">
            News & <span class="news-title-accent">Announcements</span>
          </h1>
          <p class="news-subtitle">
            Stay updated with the latest news, events, and important announcements from MUIC.
          </p>
        </div>
        <div class="news-nav-arrows">
          <button class="news-arrow" @click="scrollNews('left')" :disabled="newsScrollPos <= 0">
            <a-icon type="left" />
          </button>
          <button class="news-arrow" @click="scrollNews('right')">
            <a-icon type="right" />
          </button>
        </div>
      </div>

      <!-- Slider -->
      <div class="news-slider-track" ref="newsTrack" @mousedown="startDrag" @mousemove="onDrag" @mouseup="stopDrag"
        @mouseleave="stopDrag" @scroll="onNewsScroll">
        <div class="news-card" v-for="(news, index) in newsItems" :key="'news-' + index" @click="onNewsClick(news)">
          <div class="news-card-img">
            <img v-if="news.image && news.image !== '_x_'" :src="news.image" :alt="news.title" />
            <div v-else class="news-card-empty"><a-empty :image="false" description="No Image" /></div>
            <span class="news-card-date">{{ news.date }}</span>
          </div>
          <div class="news-card-body">
            <h3 class="news-card-title">{{ news.title }}</h3>
            <p class="news-card-desc">{{ news.description }}</p>
          </div>
        </div>
      </div>

      <!-- Dot indicators -->
      <div class="news-dots">
        <span v-for="(news, index) in newsItems" :key="'dot-' + index" class="news-dot"
          :class="{ active: currentNewsIndex === index }" @click="scrollToNews(index)"></span>
      </div>

      <!-- Remark -->
      <div class="news-remark">
        <div class="news-remark-item">
          <a-icon type="database" />
          <span>Total announcements in feed: <strong>{{ newsItems.length }} items</strong></span>
        </div>
        <div class="news-remark-item">
          <a-icon type="picture" />
          <span>Recommended image size: <strong>680 x 260 px</strong> (ratio 2.6:1)</span>
        </div>
        <div class="news-remark-item">
          <a-icon type="mail" />
          <span>To publish an announcement, please contact: <a
              href="mailto:ic-inhouse@mahidol.ac.th"><strong>ic-inhouse@mahidol.ac.th</strong></a></span>
        </div>
      </div>
    </div>

    <!-- ============================================
         MUIC Service Section
         ============================================ -->
    <div class="service-section">
      <div class="service-header">
        <div class="service-header-left">
          <div class="header-decorations">
            <span class="deco-star">✦</span>
            <span class="deco-sparkle">✧</span>
          </div>
          <h1 class="service-title">
            MUIC <span class="service-title-accent">Service</span>
          </h1>
        </div>
        <div class="service-header-right">
          <button class="service-favorite-btn" @click="goToFavorites" title="Favorites">
            <a-icon type="heart" theme="filled" />
          </button>
        </div>
      </div>

      <!-- Search & Catalog Filter -->
      <div class="service-search-wrapper">
        <div class="service-search-row">
          <a-input-search v-model="serviceSearch" placeholder="Search services..." class="service-search-input"
            allowClear />
        </div>
        <div class="service-catalog-filter">
          <a-radio-group v-model="selectedCatalog" button-style="solid" size="small">
            <a-radio-button :value="null" class="catalog-radio-btn">ALL</a-radio-button>
            <a-radio-button v-for="cat in catalogOptions" :key="'cat-' + cat.id" :value="cat.id"
              class="catalog-radio-btn">{{ cat.name }}</a-radio-button>
          </a-radio-group>
        </div>
      </div>

      <!-- Loading skeleton -->
      <div v-if="servicesLoading" class="service-loading">
        <a-row :gutter="[20, 20]">
          <a-col :xs="12" :sm="8" :md="6" :lg="4" v-for="n in 8" :key="'skel-' + n">
            <div class="service-card-skeleton">
              <div class="skeleton-icon pulse"></div>
              <div class="skeleton-title pulse"></div>
              <div class="skeleton-desc pulse"></div>
            </div>
          </a-col>
        </a-row>
      </div>

      <!-- Service cards -->
      <a-row v-else :gutter="[20, 20]" type="flex" align="stretch">
        <a-col :span="24" :xs="12" :sm="8" :md="6" :lg="4" v-for="(service, index) in filteredServices"
          :key="'svc-' + index">
          <div class="service-card" @click="onServiceClick(service)">
            <button class="service-card-fav" @click.stop="toggleFavorite(service)"
              :title="isFavorite(service) ? 'Remove from favorites' : 'Add to favorites'">
              <a-icon type="heart" :theme="isFavorite(service) ? 'filled' : 'outlined'"
                :style="{ color: isFavorite(service) ? '#eb2f96' : '#d9d9d9' }" />
            </button>
            <div class="service-icon-wrapper" :style="{ background: service.bgColor }">
              <img v-if="service.iconUrl" :src="service.iconUrl" :alt="service.title" class="service-icon-img" />
              <a-icon v-else :type="service.icon || 'appstore'" class="service-icon"
                :style="{ color: service.iconColor }" />
            </div>
            <h3 class="service-card-title">{{ service.title }}</h3>
            <p class="service-card-desc">{{ service.description }}</p>
          </div>
        </a-col>
      </a-row>
    </div>

    <!-- News Modal -->
    <modal-news :visible="showNewsModal" :news="selectedNews" @close="showNewsModal = false" />

    <!-- Service Modal -->
    <modal-service :visible="showServiceModal" :service="selectedService" @close="showServiceModal = false" />
  </div>
</template>

<script>
import modalService from "./modalService.vue";
import modalNews from "./modalNews.vue";
import { parseDetailImage, parseDetailContent, formatTagName } from "./announcementUtils";

const tableColumns = [
  {
    title: "APPLICATIONS",
    dataIndex: "applications",
    scopedSlots: { customRender: "applications" },
    width: "80%",
  },
  {
    title: "URL",
    dataIndex: "url",
    scopedSlots: { customRender: "url" },
    className: "text-center",
  },
];
export default {
  components: {
    modalNews,
    modalService,
  },
  data() {
    return {
      tableColumns,
      itemCatalog: [],
      itemBookmarks: [],
      newsItems: [],
      showNewsModal: false,
      selectedNews: null,
      serviceSearch: '',
      selectedCatalog: null,
      catalogOptions: [],
      showServiceModal: false,
      selectedService: null,
      favoriteServices: [],
      servicesLoading: false,
      services: [],
      currentNewsIndex: 0,
      newsScrollPos: 0,
      isDragging: false,
      dragStartX: 0,
      dragScrollLeft: 0,
      autoSlideTimer: null,
    };
  },
  computed: {
    filteredServices() {
      let result = this.services;
      // Filter by catalog
      if (this.selectedCatalog !== null) {
        result = result.filter(s => s.catalogId === this.selectedCatalog);
      }
      // Filter by search keyword
      if (this.serviceSearch) {
        const keyword = this.serviceSearch.toLowerCase();
        result = result.filter(s =>
          s.title.toLowerCase().includes(keyword) ||
          s.description.toLowerCase().includes(keyword)
        );
      }
      return result;
    },
  },
  mounted() {
    this.setUpmethos();
    this.startAutoSlide();
    this.fetchServices();
    this.fetchAnnouncements();
  },
  beforeDestroy() {
    this.stopAutoSlide();
  },
  methods: {
    async fetchAnnouncements() {
      try {
        const res = await this.$http.get('/portal/announcement/');
        const announcements = res.data || [];
        // Sort by priority descending
        announcements.sort((a, b) => (b.priority || 0) - (a.priority || 0));
        this.newsItems = announcements.map(item => {
          return {
            id: item.id,
            title: item.topic || '',
            description: '',
            date: this.formatDate(item.created_at),
            image: this.parseDetailImage(item.detail),
            tags: Array.from(new Set((item.use_group_detail || []).map(g => this.formatTagName(g.name)).filter(Boolean))),
            expireDate: this.formatDate(item.show_end),
            content: this.parseDetailContent(item.detail),
          };
        });
      } catch (e) {
        console.log('PROBLEM FETCHING ANNOUNCEMENTS:', e);
      }
    },
    parseDetailImage(detail) {
      return parseDetailImage(detail);
    },
    parseDetailContent(detail) {
      return parseDetailContent(detail);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    formatTagName(name) {
      return formatTagName(name);
    },
    async setUpmethos() {
      this.itemCatalog = [];
      this.itemBookmarks = [];
      const fetchBooking = this.$http.get(`/portal/bookmark/`);
      const fetchCatalog = this.$http.get(`/portal/catalog/`);
      Promise.all([fetchBooking, fetchCatalog])
        .then((response) => {
          return Promise.all(response.map((r) => r.data));
        })
        .then(([listBookmark, catalog]) => {
          this.itemCatalog = catalog.map((item) => {
            return {
              id: item.id,
              name: item.name,
              icon: item.icon,
              priority: item.priority,
            };
          });
          this.itemCatalog.sort((a, b) => {
            return b.priority - a.priority;
          });
          this.itemBookmarks = listBookmark.map((item) => {
            return {
              id: item.id,
              menu_detail: item.menu_detail,
              menu: item.menu,
            };
          });
        })
        .catch((e) => {
          console.log("PROMBLEM CONNECT API ERROR !!!");
        });
    },
    async fetchServices() {
      const colorPalette = [
        { bgColor: '#e6f7ff', iconColor: '#1890ff' },
        { bgColor: '#f6ffed', iconColor: '#52c41a' },
        { bgColor: '#fff7e6', iconColor: '#fa8c16' },
        { bgColor: '#f9f0ff', iconColor: '#722ed1' },
        { bgColor: '#e6fffb', iconColor: '#13c2c2' },
        { bgColor: '#fff1f0', iconColor: '#f5222d' },
        { bgColor: '#fcffe6', iconColor: '#a0d911' },
        { bgColor: '#fff0f6', iconColor: '#eb2f96' },
      ];
      this.servicesLoading = true;
      try {
        const catalogRes = await this.$http.get('/portal/catalog/');
        const catalogs = catalogRes.data;

        // Populate catalog filter options (sorted by priority desc)
        this.catalogOptions = catalogs
          .map(cat => ({ id: cat.id, name: cat.name, priority: cat.priority || 0 }))
          .sort((a, b) => b.priority - a.priority);
        // Fetch menus for all catalogs in parallel
        const menuPromises = catalogs.map(cat =>
          this.$http.get(`/portal/menu/?catalog=${cat.id}`).then(res => res.data)
        );
        const menuResults = await Promise.all(menuPromises);
        // Flatten all menu items
        const allMenus = menuResults.flat();
        // Sort by priority descending
        allMenus.sort((a, b) => (b.priority || 0) - (a.priority || 0));
        // Map to service card format
        this.services = allMenus.map((item, idx) => {
          const colors = colorPalette[idx % colorPalette.length];
          return {
            id: item.id,
            iconUrl: item.icon || null,
            title: item.name,
            description: this.getServiceDescription(item.name),
            bgColor: colors.bgColor,
            iconColor: colors.iconColor,
            link: item.link || '',
            htmlDescription: item.detail || '',
            priority: item.priority || 0,
            catalogId: item.catalog,
            useGroup: item.use_group_detail || [],
          };
        });
      } catch (e) {
        console.log('PROBLEM FETCHING SERVICES:', e);
      } finally {
        this.servicesLoading = false;
      }
    },
    getServiceDescription(name) {
      if (!name) return '';
      const n = name.toLowerCase();
      if (n.includes('procurement') || n.includes('จัดซื้อ')) return 'Procurement & purchasing';
      if (n.includes('academic') || n.includes('วิชาการ')) return 'Academic resources & info';
      if (n.includes('hr') || n.includes('human')) return 'Human resources services';
      if (n.includes('finance') || n.includes('การเงิน')) return 'Financial services & reports';
      if (n.includes('it') || n.includes('computer') || n.includes('tech')) return 'IT & technical support';
      if (n.includes('library') || n.includes('ห้องสมุด')) return 'Library & research resources';
      if (n.includes('student') || n.includes('นักศึกษา')) return 'Student services & support';
      if (n.includes('email') || n.includes('mail')) return 'Email & communication';
      if (n.includes('schedule') || n.includes('ตาราง')) return 'Schedules & timetables';
      if (n.includes('document') || n.includes('form') || n.includes('เอกสาร')) return 'Forms & documents';
      if (n.includes('research') || n.includes('วิจัย')) return 'Research & development';
      if (n.includes('service')) return 'University services';
      return 'Access ' + name;
    },
    onServiceClick(service) {
      this.selectedService = service;
      this.showServiceModal = true;
    },
    toggleFavorite(service) {
      const idx = this.favoriteServices.indexOf(service.title);
      if (idx >= 0) {
        this.favoriteServices.splice(idx, 1);
      } else {
        this.favoriteServices.push(service.title);
      }
    },
    isFavorite(service) {
      return this.favoriteServices.includes(service.title);
    },
    goToFavorites() {
      this.serviceSearch = '';
      // Scroll to service section
      const el = document.querySelector('.service-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },
    onNewsClick(news) {
      this.selectedNews = news;
      this.showNewsModal = true;
    },
    scrollNews(direction) {
      const track = this.$refs.newsTrack;
      if (!track) return;
      const cardWidth = 300 + 20; // card width + gap
      const scrollAmount = direction === 'left' ? -cardWidth * 2 : cardWidth * 2;
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    },
    scrollToNews(index) {
      const track = this.$refs.newsTrack;
      if (!track) return;
      const cardWidth = 300 + 20;
      track.scrollTo({ left: index * cardWidth, behavior: 'smooth' });
    },
    onNewsScroll() {
      const track = this.$refs.newsTrack;
      if (!track) return;
      this.newsScrollPos = track.scrollLeft;
      const cardWidth = 300 + 20;
      this.currentNewsIndex = Math.round(track.scrollLeft / cardWidth);
    },
    startDrag(e) {
      this.isDragging = true;
      const track = this.$refs.newsTrack;
      this.dragStartX = e.pageX - track.offsetLeft;
      this.dragScrollLeft = track.scrollLeft;
      track.style.cursor = 'grabbing';
    },
    onDrag(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const track = this.$refs.newsTrack;
      const x = e.pageX - track.offsetLeft;
      const walk = (x - this.dragStartX) * 1.5;
      track.scrollLeft = this.dragScrollLeft - walk;
    },
    stopDrag() {
      this.isDragging = false;
      const track = this.$refs.newsTrack;
      if (track) track.style.cursor = 'grab';
    },
    startAutoSlide() {
      this.autoSlideTimer = setInterval(() => {
        const track = this.$refs.newsTrack;
        if (!track) return;
        const maxScroll = track.scrollWidth - track.clientWidth;
        if (track.scrollLeft >= maxScroll - 10) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.scrollNews('right');
        }
      }, 5000);
    },
    stopAutoSlide() {
      if (this.autoSlideTimer) {
        clearInterval(this.autoSlideTimer);
        this.autoSlideTimer = null;
      }
    },
    addBookmark(...args) {
      this.itemBookmarks = [];
      this.itemBookmarks.push(args[0]);
    },
    openNotification(...args) {
      this.$notification.open({
        style: {
          backgroundColor: args[0].backgroundColor,
          color: args[0].color,
        },
        message: function (h) {
          return h("div", [h("strong", { class: "" }, args[0].title)]);
        },
        description: args[0].msg,
      });
    },
  },
};
</script>

<style scoped>
/* ============================================
   Main Wrapper
   ============================================ */
.mainportal-wrapper {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

/* ============================================
   MUIC Service Section
   ============================================ */
.service-section {
  padding: 0px 20px;
  /* background: #eaeaec; */
  border-radius: 16px;
}

.service-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.service-header-left {
  text-align: left;
}

.service-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
}

.service-favorite-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #f0f0f0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  color: #eb2f96;
  font-size: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.service-favorite-btn:hover {
  background: #fff0f6;
  border-color: #eb2f96;
  transform: scale(1.1);
}

/* Search & Catalog Filter */
.service-search-wrapper {
  margin-bottom: 24px;
}

.service-search-row {
  max-width: 400px;
  margin-bottom: 12px;
}

.service-search-input {
  border-radius: 10px !important;
}

.service-catalog-filter {
  margin-bottom: 4px;
}

.service-catalog-filter .ant-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.service-catalog-filter .ant-radio-button-wrapper {
  border-radius: 20px !important;
  font-size: 12px;
  font-weight: 600;
  padding: 0 14px;
  height: 30px;
  line-height: 28px;
  border: 1px solid #d9d9d9;
  transition: all 0.25s ease;
}

.service-catalog-filter .ant-radio-button-wrapper:not(:first-child)::before {
  display: none;
}

.service-catalog-filter .ant-radio-button-wrapper-checked {
  background: #502b80 !important;
  border-color: #502b80 !important;
  color: #fff !important;
  box-shadow: 0 2px 6px rgba(80, 43, 128, 0.3);
}

.service-catalog-filter .ant-radio-button-wrapper:hover:not(.ant-radio-button-wrapper-checked) {
  color: #502b80;
  border-color: #502b80;
}

/* (service-header styles moved above) */

.service-title {
  font-size: 36px;
  font-weight: 800;
  color: #502b80;
  margin-bottom: 10px;
  line-height: 1.3;
}

.service-title-accent {
  color: #502b80;
  font-style: italic;
  font-family: 'Georgia', serif;
  font-weight: 800;
}

.service-subtitle {
  color: #6b7280;
  font-size: 15px;
  max-width: 480px;
  margin: 0 auto;
  line-height: 1.6;
}

/* Service Card */
.service-card {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 28px 18px 22px;
  text-align: center;
  cursor: pointer;
  border: 1px solid #eef2f7;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.service-card-fav {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  transition: transform 0.2s ease;
  z-index: 2;
}

.service-card-fav:hover {
  transform: scale(1.3);
}

.service-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 28px rgba(24, 144, 255, 0.12);
  border-color: #91d5ff;
}

.service-icon-wrapper {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  transition: transform 0.3s ease;
}

.service-card:hover .service-icon-wrapper {
  transform: scale(1.1);
}

.service-icon {
  font-size: 26px !important;
}

.service-icon-img {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 6px;
}

.service-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 6px;
  line-height: 1.4;
}

.service-card-desc {
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
  margin: 0;
}

/* Skeleton Loading */
.service-card-skeleton {
  background: #fff;
  border-radius: 16px;
  padding: 28px 18px 22px;
  text-align: center;
  border: 1px solid #eef2f7;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.skeleton-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: #f0f0f5;
}

.skeleton-title {
  width: 80%;
  height: 14px;
  border-radius: 6px;
  background: #f0f0f5;
}

.skeleton-desc {
  width: 60%;
  height: 10px;
  border-radius: 6px;
  background: #f0f0f5;
}

.pulse {
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }
}

/* ============================================
   News Slider Section
   ============================================ */
.news-section {
  padding: 0 0 8px;
}

.news-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding: 0 4px;
}

.news-header-text {
  text-align: left;
}

.header-decorations {
  position: relative;
  display: inline-block;
  margin-bottom: -10px;
}

.deco-star {
  color: #f5a623;
  font-size: 18px;
  position: relative;
  top: -5px;
  right: 5px;
  animation: twinkle 2s ease-in-out infinite;
}

.deco-sparkle {
  color: #502b80;
  font-size: 14px;
  position: relative;
  top: 2px;
  animation: twinkle 2s ease-in-out infinite 0.5s;
}

@keyframes twinkle {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(0.8);
  }
}

.news-title {
  font-size: 32px;
  font-weight: 700;
  color: #502b80;
  margin-bottom: 8px;
  line-height: 1.3;
}

.news-title-accent {
  font-style: italic;
  color: #502b80;
  font-family: 'Georgia', serif;
}

.news-subtitle {
  color: #6b7280;
  font-size: 14px;
  max-width: 480px;
  margin: 0;
  line-height: 1.6;
}

.news-nav-arrows {
  display: flex;
  gap: 8px;
}

.news-arrow {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #d9d9d9;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #595959;
}

.news-arrow:hover:not(:disabled) {
  background: #502b80;
  border-color: #502b80;
  color: #fff;
}

.news-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Slider Track */
.news-slider-track {
  display: flex;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 8px 4px 16px;
  cursor: grab;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.news-slider-track::-webkit-scrollbar {
  display: none;
}

/* News Card */
.news-card {
  min-width: 300px;
  max-width: 300px;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #eef2f7;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;
}

.news-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(80, 43, 128, 0.15);
  border-color: #d3adf7;
}

.news-card-img {
  width: 100%;
  height: 180px;
  overflow: hidden;
  position: relative;
}

.news-card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.news-card:hover .news-card-img img {
  transform: scale(1.05);
}

.news-card-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #bfbfbf;
}

.news-card-date {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 11px;
  color: #fff;
  background: rgba(80, 43, 128, 0.8);
  padding: 3px 12px;
  border-radius: 20px;
  backdrop-filter: blur(4px);
}

.news-card-body {
  padding: 16px 18px;
}

.news-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 8px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 45px;
}

.news-card-desc {
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Dot Indicators */
.news-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 8px 0;
}

.news-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d9d9d9;
  cursor: pointer;
  transition: all 0.3s ease;
}

.news-dot.active {
  background: #502b80;
  width: 20px;
  border-radius: 4px;
}

.news-dot:hover {
  background: #b37feb;
}

/* News Remark */
.news-remark {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 12px 4px 0;
}

.news-remark-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8c8c8c;
}

.news-remark-item strong {
  color: #595959;
}

.news-remark-item a {
  color: #502b80;
  text-decoration: none;
}

.news-remark-item a:hover {
  text-decoration: underline;
}

.news-remark-item a strong {
  color: #502b80;
}

/* Responsive */
@media (max-width: 768px) {
  .news-card {
    min-width: 260px;
    max-width: 260px;
  }

  .news-card-img {
    height: 150px;
  }

  .news-header-title {
    font-size: 18px;
  }

  .news-remark {
    flex-direction: column;
    gap: 8px;
  }

  .service-title {
    font-size: 26px;
  }

  .service-section {
    padding: 24px 12px;
  }

  .service-card {
    padding: 20px 12px 16px;
  }

  .service-icon-wrapper {
    width: 50px;
    height: 50px;
  }

  .service-icon {
    font-size: 22px !important;
  }
}

@media (max-width: 480px) {
  .news-card {
    min-width: 230px;
    max-width: 230px;
  }

  .news-card-img {
    height: 130px;
  }

  .news-card-title {
    font-size: 13px;
    min-height: 38px;
  }

  .news-card-desc {
    font-size: 11px;
  }

  .news-header-title {
    font-size: 16px;
  }

  .service-title {
    font-size: 22px;
  }

  .service-subtitle {
    font-size: 13px;
  }
}
</style>